import {
	Component,
	EventEmitter,
	OnInit,
	Output,
	ViewChild
} from '@angular/core';
import { BUTTON_TYPES } from '@edassist-ui/button';
import {
	AuthenticationService,
	ContentOptions,
	ContentService,
	ParticipantService,
	User
} from '@edassist/api';
import {
	AppConfigService,
	constants,
	GeneralContentService,
	HybridAppService,
	LocalStorageService
} from '@edassist/common';
import { switchMap, tap } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { DropdownNavItemComponent } from '@edassist-ui/dropdown-nav-item';
import { Router } from '@angular/router';
import { DropdownService } from '@edassist-ui/dropdown';

@Component({
	selector: 'tu-support-services',
	templateUrl: './support-services.component.html',
	styleUrls: ['./support-services.component.scss']
})
export class SupportServicesComponent implements OnInit {
	constants = constants;
	BUTTON_TYPES = BUTTON_TYPES;

	liveChatEnabled: boolean;
	isEdAccessActive: boolean;
	allowEdAccessSupportLink: boolean;
	showSupportServicesMenu: boolean;
	edAccessInfoMenuItem: string;
	@Output() readonly messageEvent = new EventEmitter<boolean>();
	@ViewChild('dropdownContentNav')
	dropdownContentNav?: DropdownNavItemComponent;

	private readonly subscriptions = new Subscription();
	private user: User;

	constructor(
		private readonly authService: AuthenticationService,
		private readonly participantService: ParticipantService,
		private readonly hybridAppService: HybridAppService,
		private readonly contentService: ContentService,
		private readonly router: Router,
		readonly dropdownService: DropdownService
	) {}

	ngOnInit(): void {
		const client = LocalStorageService.getItem(
			constants.localStorageKeys.CLIENT_DETAILS
		);
		this.liveChatEnabled = client.liveChatEnabled;
		this.subscriptions.add(
			this.authService.currentUser.subscribe((user: User) => {
				if (user) {
					this.user = user;
					this.getParticipantAndEdAccessInfo(client.id);
				}
			})
		);
	}

	navigate(route: string): void {
		const clientId = LocalStorageService.getItem(constants.localStorageKeys.CLIENT_DETAILS)?.code;
		const newBenefitsAndSupportPage = AppConfigService.isNewBenefitsAndSupportPageEnabled(Number(clientId));
		if(newBenefitsAndSupportPage) {
			this.dropdownService.close(constants.dropdownIds.supportServices);
			this.router.navigate([`/${route}`]);
		} else {
			this.hybridAppService.navigateToUrl(route);
		}
	}

	handleSupportIconClick(): void {
		if (!this.showSupportServicesMenu) {
			this.navigate(constants.BASE_APP_ROUTES.support);
		}
	}

	handleDropdownOnKeydown(
		isAccessSupportLink: boolean,
		isLiveChat: boolean
	): void {
		if (!this.allowEdAccessSupportLink && !this.liveChatEnabled) {
			this.dropdownContentNav.toggleDropDown();
		} else if (
			isAccessSupportLink &&
			this.allowEdAccessSupportLink &&
			!this.liveChatEnabled
		) {
			this.dropdownContentNav.toggleDropDown();
		} else if (isLiveChat && this.liveChatEnabled) {
			this.dropdownContentNav.toggleDropDown();
		}
	}
	private getParticipantAndEdAccessInfo(clientId: number): void {
		this.participantService
			.getParticipant(this.user.participantId)
			.pipe(
				tap((participant) => {
					this.isEdAccessActive =
						this.user.edAccessEnabled &&
						this.user.hasEdAccessPrograms &&
						participant.generic8 !==
							constants.programFeatures.edAccess.edAccessDisabledParticipant;
					this.showSupportServicesMenu =
						this.liveChatEnabled || this.isEdAccessActive;
				}),
				switchMap(() => {
					const contentOptionsClient: ContentOptions = {
						client: clientId.toString(),
						textOnly: true
					};

					return this.contentService.findByName(
						this.constants.contentConstants.contentTypes.general,
						this.constants.contentConstants.CONTENT_COMPONENTS
							.ED_ACCESS_INFO_PAGE,
						constants.contentConstants.CONTENT_NAMES.SUPPORT_MENU_LABEL,
						contentOptionsClient
					);
				})
			)
			.subscribe((clientLevelEdAccessInfoContents) => {
				if (clientLevelEdAccessInfoContents?.length) {
					const clientLevelEdAccessInfoContent =
						GeneralContentService.mapContent(clientLevelEdAccessInfoContents);
					if (clientLevelEdAccessInfoContent?.supportMenuLabel) {
						this.edAccessInfoMenuItem =
							clientLevelEdAccessInfoContent?.supportMenuLabel;
						this.allowEdAccessSupportLink =
							this.isEdAccessActive && !this.user.edAccessProviderPageDisabled;
					}
				}
			});
	}
}
